/**
 * Locale-aware number parsing utilities for handling different decimal separators.
 *
 * HTML5 type="number" inputs only support period (.) as decimal separator,
 * which breaks for users in locales that use comma (,) as decimal separator.
 * These utilities normalize input to handle both separators correctly.
 */

/**
 * Gets the user's locale from browser settings.
 * Falls back to en-US if detection fails.
 */
export function getUserLocale(): string {
  if (typeof navigator === 'undefined') {
    return 'en-US';
  }
  return navigator.languages?.[0] || navigator.language || 'en-US';
}

/**
 * Detects the decimal separator used in the user's locale.
 * Returns ',' for locales that use comma (e.g., de-DE, fr-FR, es-ES)
 * Returns '.' for locales that use period (e.g., en-US, en-GB)
 */
export function getDecimalSeparator(locale?: string): ',' | '.' {
  const userLocale = locale || getUserLocale();

  // Use Intl.NumberFormat to detect the decimal separator
  const formatter = new Intl.NumberFormat(userLocale);
  const parts = formatter.formatToParts(1.1);
  const decimalPart = parts.find(part => part.type === 'decimal');

  return decimalPart?.value === ',' ? ',' : '.';
}

/**
 * Parses a number string that may contain locale-specific decimal separators.
 * Handles both comma and period as decimal separators.
 *
 * Examples:
 * - parseLocaleNumber("10.5") → 10.5
 * - parseLocaleNumber("10,5") → 10.5
 * - parseLocaleNumber("1,234.56") → 1234.56 (strips thousands separator)
 * - parseLocaleNumber("1.234,56") → 1234.56 (strips thousands separator)
 * - parseLocaleNumber("abc") → NaN
 *
 * @param value - The string value to parse
 * @returns The parsed number, or NaN if invalid
 */
export function parseLocaleNumber(value: string): number {
  if (!value || typeof value !== 'string') {
    return NaN;
  }

  // Trim whitespace
  const trimmed = value.trim();

  if (trimmed === '') {
    return NaN;
  }

  // Detect which separator is being used as decimal based on position
  // The decimal separator is typically the LAST occurrence of comma or period
  const lastComma = trimmed.lastIndexOf(',');
  const lastPeriod = trimmed.lastIndexOf('.');

  let normalized: string;

  if (lastComma === -1 && lastPeriod === -1) {
    // No decimal separator, just parse as integer
    normalized = trimmed;
  } else if (lastComma > lastPeriod) {
    // Comma appears after period, so comma is the decimal separator
    // Remove all periods (thousands separators) and replace comma with period
    normalized = trimmed.replace(/\./g, '').replace(',', '.');
  } else {
    // Period appears after comma (or no comma), so period is the decimal separator
    // Remove all commas (thousands separators)
    normalized = trimmed.replace(/,/g, '');
  }

  return parseFloat(normalized);
}

/**
 * Formats a number for display using the user's locale.
 * This is a convenience wrapper around existing formatNumber utility.
 *
 * @param value - The number to format
 * @param decimals - Number of decimal places to show
 * @param locale - Optional locale override
 * @returns Formatted string
 */
export function formatLocaleNumber(
  value: number,
  decimals: number = 2,
  locale?: string
): string {
  const userLocale = locale || getUserLocale();

  return new Intl.NumberFormat(userLocale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}
